import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Download, Trash2, Clock, RotateCcw, ImageIcon, Bell } from "lucide-react";

const fmtBytes = (n) => {
  if (!n) return "0 KB";
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(2)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024).toFixed(0)} KB`;
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" }) : "—");
const shortUrl = (u) => { try { const x = new URL(u); return x.hostname + x.pathname.slice(-40); } catch { return u; } };

const btn = "border border-[#27272a] text-[#fafafa] hover:border-[#c8aa6e] px-4 py-2.5 font-medium text-sm disabled:opacity-60 flex items-center gap-2";

function Stat({ label, value, sub, testid }) {
  return (
    <div className="bg-[#09090b] border border-[#27272a] px-4 py-3" data-testid={testid}>
      <div className="label-tiny">{label}</div>
      <div className="font-head text-xl text-[#c8aa6e] mt-1">{value}</div>
      {sub && <div className="text-[11px] text-[#71717a] mt-0.5">{sub}</div>}
    </div>
  );
}

function WarmupSchedule({ schedule, onChange }) {
  const [time, setTime] = useState(schedule?.time || "03:00");
  useEffect(() => { if (schedule?.time) setTime(schedule.time); }, [schedule?.time]);
  if (!schedule) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="img-warmup-schedule">
      <Clock className="w-4 h-4 text-[#c8aa6e]" />
      <span className="text-[#fafafa]">Pré-download noturno de todas as fotos</span>
      <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
        onBlur={() => time !== schedule.time && onChange({ time })}
        data-testid="img-warmup-time"
        className="bg-[#09090b] border border-[#3f3f46] text-[#fafafa] px-2 py-1 text-sm font-mono focus:border-[#c8aa6e] outline-none" />
      <button onClick={() => onChange({ enabled: !schedule.enabled })} data-testid="img-warmup-toggle"
        className={`text-xs px-3 py-1.5 border ${schedule.enabled ? "border-[#10b981] text-[#10b981]" : "border-[#3f3f46] text-[#71717a]"}`}>
        {schedule.enabled ? "Ativo" : "Desativado"}
      </button>
      <span className="text-xs text-[#71717a]" data-testid="img-warmup-next">
        {schedule.enabled && schedule.next_run ? `Próxima execução: ${fmtDate(schedule.next_run)} (UTC)` : "Horas em UTC"}
      </span>
    </div>
  );
}

function RecentChanges({ items }) {
  if (!items?.length) return null;
  return (
    <div data-testid="img-recent-changes">
      <div className="label-tiny mb-2">Fotos substituídas na origem (atualizadas automaticamente)</div>
      <div className="divide-y divide-[#27272a] border border-[#27272a] bg-[#09090b] max-h-40 overflow-y-auto">
        {items.map((c, i) => (
          <div key={`${c.url}-${c.at}`} className="px-3 py-1.5 text-xs flex items-center gap-3" data-testid={`img-change-${i}`}>
            <span className="text-[#71717a] font-mono shrink-0">{fmtDate(c.at)}</span>
            <span className="text-[#fafafa] truncate flex-1" title={c.url}>{shortUrl(c.url)}</span>
            <span className="text-[#a1a1aa] shrink-0">{fmtBytes(c.old_size)} → {fmtBytes(c.new_size)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BySource({ rows }) {
  if (!rows?.length) return null;
  return (
    <div data-testid="img-by-source">
      <div className="label-tiny mb-2">Por origem (acertos · falhas · erros · banda · latência)</div>
      <div className="border border-[#27272a] bg-[#09090b] overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-[#71717a] text-left">
            <tr className="border-b border-[#27272a]">
              <th className="px-3 py-2 font-normal">Origem</th>
              <th className="px-3 py-2 font-normal text-right">Acertos</th>
              <th className="px-3 py-2 font-normal text-right">Falhas</th>
              <th className="px-3 py-2 font-normal text-right">Erros</th>
              <th className="px-3 py-2 font-normal text-right">Taxa</th>
              <th className="px-3 py-2 font-normal text-right">Descarregado</th>
              <th className="px-3 py-2 font-normal text-right">Servido da cache</th>
              <th className="px-3 py-2 font-normal text-right">Latência média</th>
              <th className="px-3 py-2 font-normal">Último erro</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const slow = r.avg_ms > 2000; const failing = r.errors > 0 && r.errors >= (r.downloads || 0) * 0.3;
              return (
                <tr key={r.source} className="border-b border-[#27272a] last:border-0" data-testid={`img-source-${r.source}`}>
                  <td className="px-3 py-1.5 text-[#fafafa]">{r.name || r.source}</td>
                  <td className="px-3 py-1.5 text-right text-[#a1a1aa] font-mono">{r.hits}</td>
                  <td className="px-3 py-1.5 text-right text-[#a1a1aa] font-mono">{r.misses}</td>
                  <td className={`px-3 py-1.5 text-right font-mono ${failing ? "text-[#ef4444]" : "text-[#a1a1aa]"}`}>{r.errors}</td>
                  <td className="px-3 py-1.5 text-right text-[#c8aa6e] font-mono">{(r.hit_ratio * 100).toFixed(0)}%</td>
                  <td className="px-3 py-1.5 text-right text-[#a1a1aa] font-mono">{fmtBytes(r.bytes_download)}</td>
                  <td className="px-3 py-1.5 text-right text-[#a1a1aa] font-mono">{fmtBytes(r.bytes_hit)}</td>
                  <td className={`px-3 py-1.5 text-right font-mono ${slow ? "text-[#f59e0b]" : "text-[#a1a1aa]"}`}>{r.avg_ms} ms</td>
                  <td className="px-3 py-1.5 text-[#71717a] truncate max-w-[220px]" title={r.last_error_at ? fmtDate(r.last_error_at) : ""}>{r.last_error || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AlertsConfig({ alerts, onChange, onTest }) {
  const cfg = alerts?.config;
  const [email, setEmail] = useState(cfg?.email || "");
  const [threshold, setThreshold] = useState(cfg?.threshold_pct ?? 90);
  const [runs, setRuns] = useState(cfg?.failed_runs ?? 2);
  useEffect(() => { if (cfg) { setEmail(cfg.email || ""); setThreshold(cfg.threshold_pct); setRuns(cfg.failed_runs); } }, [cfg]);
  if (!cfg) return null;
  const inp = "bg-[#09090b] border border-[#3f3f46] text-[#fafafa] px-2 py-1 text-sm focus:border-[#c8aa6e] outline-none";
  const last = alerts.history?.[0];
  return (
    <div className="space-y-2" data-testid="img-alerts">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <Bell className="w-4 h-4 text-[#c8aa6e]" />
        <span className="text-[#fafafa]">Alertas por email</span>
        <button onClick={() => onChange({ enabled: !cfg.enabled })} data-testid="img-alerts-toggle"
          className={`text-xs px-3 py-1.5 border ${cfg.enabled ? "border-[#10b981] text-[#10b981]" : "border-[#3f3f46] text-[#71717a]"}`}>
          {cfg.enabled ? "Ativo" : "Desativado"}
        </button>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@…"
          onBlur={() => email !== cfg.email && onChange({ email })} data-testid="img-alerts-email" className={`${inp} w-56`} />
        <label className="text-xs text-[#a1a1aa] flex items-center gap-1">disco ≥
          <input type="number" min={50} max={100} value={threshold} onChange={(e) => setThreshold(e.target.value)}
            onBlur={() => Number(threshold) !== cfg.threshold_pct && onChange({ threshold_pct: Number(threshold) })}
            data-testid="img-alerts-threshold" className={`${inp} w-16 font-mono`} />%
        </label>
        <label className="text-xs text-[#a1a1aa] flex items-center gap-1">warm-up falhado
          <input type="number" min={1} max={10} value={runs} onChange={(e) => setRuns(e.target.value)}
            onBlur={() => Number(runs) !== cfg.failed_runs && onChange({ failed_runs: Number(runs) })}
            data-testid="img-alerts-runs" className={`${inp} w-14 font-mono`} />× seguidas
        </label>
        <button onClick={onTest} className="text-xs px-3 py-1.5 border border-[#27272a] text-[#fafafa] hover:border-[#c8aa6e]" data-testid="img-alerts-test">
          Enviar email de teste
        </button>
      </div>
      <div className="text-xs text-[#71717a]" data-testid="img-alerts-status">
        {!alerts.resend_configured && <span className="text-[#f59e0b]">Resend não configurado (Definições → Email) — os alertas ficam registados mas não são enviados. </span>}
        {last ? `Último alerta: ${fmtDate(last.at)} · ${last.subject} · ${last.email_status === "success" ? "enviado" : `não enviado (${last.message})`}` : "Nenhum alerta enviado ainda."}
        {alerts.consecutive_failed_warmups > 0 && ` · ${alerts.consecutive_failed_warmups} warm-up(s) falhado(s) seguido(s)`}
      </div>
    </div>
  );
}

export default function ImgCachePanel() {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/img-cache").then(({ data }) => setStats(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const running = !!stats?.warmup?.running;
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [running, load]);

  const clear = async () => {
    if (!window.confirm("Limpar a cache de imagens? As fotos serão novamente descarregadas das origens na próxima visualização. Nenhum dado de perfis ou histórico é afetado.")) return;
    setBusy(true);
    try {
      const { data } = await api.delete("/admin/img-cache");
      toast.success(`Cache de imagens limpa: ${data.removed} ficheiro(s) removido(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao limpar cache de imagens");
    } finally { setBusy(false); }
  };
  const warmup = async (all) => {
    try {
      const { data } = await api.post("/admin/img-cache/warmup", null, { params: all ? { all: true } : { limit: 300 } });
      if (data.status === "already_running") toast.info("Pré-carregamento já em curso");
      else toast.info(all ? "Pré-carregamento de todas as fotos iniciado" : "Pré-carregamento iniciado (300 perfis mais recentes)");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao iniciar pré-carregamento");
    }
  };
  const resetCounters = async () => {
    try { await api.post("/admin/img-cache/reset-counters"); toast.success("Contadores reiniciados"); load(); }
    catch (e) { toast.error("Erro ao reiniciar contadores"); }
  };
  const saveAlerts = async (patch) => {
    try {
      const { data } = await api.put("/admin/img-cache/alerts", patch);
      setStats((s) => ({ ...s, alerts: { ...s.alerts, config: data } }));
      toast.success("Alertas atualizados");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar alertas");
    }
  };
  const testAlert = async () => {
    try {
      const { data } = await api.post("/admin/img-cache/alerts/test");
      if (data.status === "success") toast.success(data.message); else toast.error(data.message);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar email de teste");
    }
  };
  const saveSchedule = async (patch) => {
    try {
      const { data } = await api.put("/admin/img-cache/schedule", patch);
      setStats((s) => ({ ...s, schedule: data }));
      toast.success(data.enabled ? `Pré-download noturno ativo às ${data.time} UTC` : "Pré-download noturno desativado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar agendamento");
    }
  };

  const c = stats?.counters || {};
  const w = stats?.warmup;
  const pct = stats ? Math.min(100, (stats.bytes / stats.max_bytes) * 100) : 0;
  const served = (c.hits || 0) + (c.misses || 0);

  return (
    <div className="bg-[#18181b] border border-[#27272a] p-5 space-y-4" data-testid="admin-img-cache">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2"><ImageIcon className="w-4 h-4 text-[#c8aa6e]" /><h3 className="font-head text-base text-[#fafafa]">Cache de imagens (disco)</h3></div>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => warmup(false)} disabled={running} className={btn} data-testid="admin-img-cache-warmup-btn">
            <Download className="w-4 h-4" /> Pré-carregar recentes
          </button>
          <button onClick={() => warmup(true)} disabled={running} className={btn} data-testid="admin-img-cache-warmup-all-btn">
            <Download className="w-4 h-4" /> Pré-carregar todas
          </button>
          <button onClick={clear} disabled={busy} className={btn} data-testid="admin-clear-img-cache">
            <Trash2 className="w-4 h-4" /> {busy ? "A limpar…" : "Limpar cache de imagens"}
          </button>
        </div>
      </div>
      {!stats ? <div className="text-xs text-[#71717a]">A carregar…</div> : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat testid="admin-img-cache-files" label="Ficheiros em cache" value={stats.files}
              sub={`TTL ${Math.round(stats.ttl_seconds / 86400)} dias · miniaturas ${stats.thumb_widths?.join("/")}px`} />
            <Stat testid="admin-img-cache-size" label="Espaço em disco" value={fmtBytes(stats.bytes)} sub={`limite ${fmtBytes(stats.max_bytes)}`} />
            <Stat testid="admin-img-cache-ratio" label="Taxa de acertos" value={served ? `${(c.hit_ratio * 100).toFixed(1)}%` : "—"}
              sub={`${c.hits || 0} acertos · ${c.misses || 0} falhas · ${c.errors || 0} erros`} />
            <Stat testid="admin-img-cache-saved" label="Banda poupada" value={fmtBytes((c.bytes_hit || 0) + (c.bytes_thumb_saved || 0))}
              sub={`${fmtBytes(c.bytes_hit)} sem re-download · ${fmtBytes(c.bytes_thumb_saved)} por miniaturas`} />
          </div>
          <div className="h-1 w-full bg-[#27272a]"><div className="h-1 bg-[#c8aa6e] transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
          <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-[#71717a]">
            <span data-testid="admin-img-cache-warmup">
              {running
                ? `Pré-carregamento em curso… ${w.progress ? `${w.progress.done}/${w.progress.total}` : ""}`
                : w?.last
                  ? `Último pré-carregamento (${w.last.reason}, ${fmtDate(w.last.at)}): ${w.last.downloaded} novas, ${w.last.refreshed} substituídas, ${w.last.unchanged + w.last.fresh} já em cache${w.last.failed ? `, ${w.last.failed} falhas` : ""} · ${w.last.seconds}s`
                  : "Após cada recolha, as fotos dos novos perfis são pré-carregadas automaticamente."}
            </span>
            <span className="flex items-center gap-3">
              <span data-testid="admin-img-cache-revalidated">{c.revalidated || 0} verificações na origem · {c.refreshed || 0} fotos substituídas · desde {fmtDate(c.since)}</span>
              <button onClick={resetCounters} className="inline-flex items-center gap-1 hover:text-[#c8aa6e]" data-testid="admin-img-cache-reset" title="Reiniciar contadores">
                <RotateCcw className="w-3 h-3" /> reiniciar
              </button>
            </span>
          </div>
          <WarmupSchedule schedule={stats.schedule} onChange={saveSchedule} />
          <AlertsConfig alerts={stats.alerts} onChange={saveAlerts} onTest={testAlert} />
          <BySource rows={stats.by_source} />
          <RecentChanges items={stats.recent_changes} />
        </>
      )}
    </div>
  );
}
